<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MetaBusinessAgentAPI from 'dashboard/api/metaBusinessAgent';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ResourceList from './ResourceList.vue';

const props = defineProps({
  hookId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const T = key => t(`INTEGRATION_SETTINGS.META_BUSINESS_AGENT.SKILLS.${key}`);

const skills = ref([]);
const showForm = ref(false);
const title = ref('');
const description = ref('');
const skill = ref('');
const pendingDelete = ref(null);
const showDeleteModal = ref(false);

const fetchSkills = async () => {
  try {
    const { data } = await MetaBusinessAgentAPI.listSkills(props.hookId);
    skills.value = data;
  } catch (error) {
    useAlert(T('API.FETCH_ERROR'));
  }
};

onMounted(fetchSkills);

const resetForm = () => {
  title.value = '';
  description.value = '';
  skill.value = '';
  showForm.value = false;
};

const createSkill = async () => {
  try {
    await MetaBusinessAgentAPI.createSkill(props.hookId, {
      title: title.value,
      description: description.value,
      skill: skill.value,
    });
    useAlert(T('API.CREATE_SUCCESS'));
    resetForm();
    fetchSkills();
  } catch (error) {
    useAlert(T('API.CREATE_ERROR'));
  }
};

const confirmDelete = item => {
  pendingDelete.value = item;
  showDeleteModal.value = true;
};

const deleteSkill = async () => {
  try {
    await MetaBusinessAgentAPI.deleteSkill(
      props.hookId,
      pendingDelete.value.id
    );
    useAlert(T('API.DELETE_SUCCESS'));
    fetchSkills();
  } catch (error) {
    useAlert(T('API.DELETE_ERROR'));
  } finally {
    showDeleteModal.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex justify-end">
      <Button :label="T('ADD_BUTTON')" @click="showForm = true" />
    </div>
    <div
      v-if="showForm"
      class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-1"
    >
      <Input
        v-model="title"
        :label="T('SKILL_TITLE_LABEL')"
        placeholder="greeting-skill"
      />
      <Input v-model="description" :label="T('SKILL_DESCRIPTION_LABEL')" />
      <TextArea
        v-model="skill"
        :label="T('SKILL_BODY_LABEL')"
        :max-length="20000"
      />
      <div class="flex justify-end gap-2">
        <Button
          slate
          faded
          :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
          @click="resetForm"
        />
        <Button
          :label="$t('INTEGRATION_APPS.ADD.FORM.SUBMIT')"
          @click="createSkill"
        />
      </div>
    </div>
    <ResourceList
      :items="skills"
      :empty-state="T('EMPTY_STATE')"
      @delete="confirmDelete"
    >
      <template #default="{ item }">
        <p class="font-medium text-n-slate-12 m-0">{{ item.title }}</p>
        <p class="text-sm text-n-slate-11 m-0">{{ item.description }}</p>
      </template>
    </ResourceList>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
      :on-confirm="deleteSkill"
      :title="T('DELETE_CONFIRM_TITLE')"
      :message="T('DELETE_CONFIRM_MESSAGE')"
      :confirm-text="$t('INTEGRATION_APPS.DELETE.CONFIRM_BUTTON_TEXT.INBOX')"
      :reject-text="$t('INTEGRATION_APPS.DELETE.CANCEL_BUTTON_TEXT')"
    />
  </div>
</template>
